import type { MapLocation, MapVault, VaultCopy } from './GlobalMap'

/**
 * Adapters from the existing map endpoints to GlobalMap's props.
 *
 * The endpoints already return what the map needs; only the shape differs, and it differs in
 * one way that matters: they speak `latitude`/`longitude` as separate numbers while every geo
 * library here takes a single `[longitude, latitude]` pair. Doing that conversion in exactly
 * one place is the point of this file — a marker in the Gulf of Guinea means someone swapped
 * them, and there should only ever be one line where that can happen.
 */

/** The shape /api/vaults/paths and /api/locations/map return. */
export interface ApiMapNode {
    id: number | string
    name: string
    type: 'dc' | 'vault'
    latitude: number | null
    longitude: number | null
    address?: string
    category?: string
    vaultId?: string
    region?: string
    status?: string
    /** IANA zone from WorldLocations.timezone. Locations only — vaults get no clock. */
    timeZone?: string | null
    objectCount?: number
    versionCount?: number
    totalSize?: number | string
}

export interface ApiMapConnection {
    fromLocationId: number | string
    toLocationId: number | string
    objectCount?: number
    versionCount?: number
    totalSize?: number
    bandwidth?: number
    latencyPercentage?: number
    tcpPercentage?: number
    encryptionPercentage?: number
}

/** [longitude, latitude], or null when the node cannot be plotted. The ONE conversion point. */
function coordsOf(n: ApiMapNode): [number, number] | null {
    if (n.latitude == null || n.longitude == null) return null
    const lat = Number(n.latitude)
    const lon = Number(n.longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
    // Out-of-range values project to nonsense rather than failing, so reject them here.
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null
    return [lon, lat]
}

function humanSize(bytes?: number | string): string {
    const n = typeof bytes === 'string' ? Number(bytes) : bytes
    if (!n || !Number.isFinite(n)) return '—'
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
    let v = n
    let i = 0
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1 }
    return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`
}

/**
 * Locations, INCLUDING those without coordinates.
 *
 * The old call sites filtered those out before the map ever saw them, which is why the estate
 * appeared complete while roughly two fifths of the objects were missing. GlobalMap needs them
 * to count what it cannot plot.
 */
export function toMapLocations(nodes: ApiMapNode[] | undefined): MapLocation[] {
    return (nodes || [])
        .filter((n) => n.type !== 'vault')
        .map((n) => ({
            id: String(n.id),
            name: n.name || 'Unknown',
            coords: coordsOf(n),
            // Empty, not an em-dash: the map decides how to label "no category", and a
            // dash injected here surfaced as a legend row reading "—".
            category: n.category || '',
            place: n.address || '',
            objects: Number(n.objectCount || 0),
            timeZone: n.timeZone || null,
        }))
}

/** Vaults. A vault with no coordinates cannot be a marker, so it is dropped here. */
export function toMapVaults(nodes: ApiMapNode[] | undefined): MapVault[] {
    return (nodes || [])
        .filter((n) => n.type === 'vault')
        .map((n) => ({ node: n, coords: coordsOf(n) }))
        .filter((x): x is { node: ApiMapNode; coords: [number, number] } => x.coords !== null)
        .map(({ node, coords }) => ({
            id: String(node.id),
            vaultId: node.vaultId || String(node.id),
            name: node.name || 'Vault',
            coords,
            region: node.region || '',
            status: node.status === 'inactive' ? 'inactive' : 'active',
            objects: Number(node.objectCount || 0),
            versions: Number(node.versionCount || 0),
            size: humanSize(node.totalSize),
        }))
}

/**
 * Copy edges, carrying the per-path measurements through to the tooltip.
 *
 * No filtering against the node lists: GlobalMap already skips an edge whose endpoints are not
 * present, so a filtered screen does not have to prune this first.
 */
export function toVaultCopies(connections: ApiMapConnection[] | undefined): VaultCopy[] {
    return (connections || []).map((c) => ({
        locationId: String(c.fromLocationId),
        vaultId: String(c.toLocationId),
        bandwidth: c.bandwidth ?? null,
        latencyPercentage: c.latencyPercentage ?? null,
        tcpPercentage: c.tcpPercentage ?? null,
        encryptionPercentage: c.encryptionPercentage ?? null,
    }))
}
